import isEmpty from 'ramda/src/isEmpty';
import isNil from 'ramda/src/isNil';
import { titleCase } from 'voca';

import { HealthArticleUGCVirtualListValue } from '../../../components/HealthArticleUGCVirtualList';
import { ArticleAdviceCardHeaderValue } from '../../../components/ArticleAdviceCardHeader';
import { ArticleAdviceQuestionValue } from '../../../components/ArticleAdviceQuestion';
import { ArticleAdviceAnswerValue } from '../../../components/ArticleAdviceAnswer';
import { ArticleAdviceResponseValue } from '../../../components/ArticleAdviceResponse';
import {
  HealthArticleSectionHealthExperienceListValue,
} from '../../../components/HealthArticleSectionHealthExperienceList';

import { snakeToCamelCase } from '../../../utils';
import { HealthArticleResponse } from '../../../types/api';
import {
  HealthArticleData,
  VeterinaryAdviceQuestion,
  VeterinaryAdviceAnswer,
  VeterinaryAdviceQuestionComment,
  VeterinaryAdviceHealthExperience,
} from '../../../types/app';
import { UnpackRecord } from '../../../types';
import {
  formatter,
  userLabelParser,
} from '../../../utils/formatter';

type VeterinaryAdviceWithoutTitleInput = Omit<UnpackRecord<Pick<HealthArticleResponse, 'veterinary_advice'>>, 'title'>;
type VeterinaryAdviceWithoutTitleOutput = Omit<UnpackRecord<Pick<HealthArticleData, 'veterinaryAdvice'>>, 'title'>;
type Input = Record<'next_page' | 'prev_page', (null | number)>
  & VeterinaryAdviceWithoutTitleInput;

type Output = Record<'nextPage' | 'prevPage', (null | number)> & {
  adviceList: HealthArticleUGCVirtualListValue;
  healthExperienceList: HealthArticleSectionHealthExperienceListValue;
};

const userLabelFormatter = formatter({
  parser: userLabelParser,
});

const mapSymptomList = (symptoms: string[]) => (
  symptoms.map((symptom) => titleCase(symptom))
);

const mapToVeterinaryAdvice = (
  {
    health_experiences: healthExperiences,
    questions,
  }: VeterinaryAdviceWithoutTitleInput,
): VeterinaryAdviceWithoutTitleOutput => ({
  healthExperiences: healthExperiences.map(({
    structured_data: structuredData,
    ...otherHealthExperience
  }) => ({
    ...snakeToCamelCase(otherHealthExperience),
    structuredData,
  })),
  questions: questions.map(({
    structured_data: structuredData,
    ...otherQuestion
  }) => ({
    ...snakeToCamelCase(otherQuestion),
    structuredData,
  })),
});

const mapQuestionsToAdviceList = (
  {
    questions,
  }: Omit<UnpackRecord<Pick<HealthArticleData, 'veterinaryAdvice'>>, 'title'>,
): HealthArticleUGCVirtualListValue => {
  const veterinaryAdviceToAdviceHeader = ({
    age: ageLabel,
    breed,
    petName: name,
    numHelpful,
    upvoteApiUrl,
    downvoteApiUrl,
  }: VeterinaryAdviceQuestion): ArticleAdviceCardHeaderValue => ({
    ageLabel,
    breed,
    name,
    numberOfVotes: numHelpful,
    upvoteApiUrl,
    downvoteApiUrl,
  });

  const veterinaryAdviceToAdviceQuestion = ({
    text,
    petName,
    datePosted: dateLabel,
  }: VeterinaryAdviceQuestion): ArticleAdviceQuestionValue => ({
    text,
    userLabel: userLabelFormatter(petName),
    dateLabel,
  });

  const veterinaryAdviceToAdviceAnswer = ({
    vetName: name,
    body: text,
    vetImageUrl: url,
    datePosted: dateLabel,
    numRecommendations: numberOfRecommendations,
  }: VeterinaryAdviceAnswer): ArticleAdviceAnswerValue => ({
    name,
    text,
    url,
    dateLabel,
    numberOfRecommendations,
  });

  const veterinaryAdviceToAdviceResponse = ({
    author: userLabel,
    body: text,
    datePosted: dateLabel,
  }: VeterinaryAdviceQuestionComment): ArticleAdviceResponseValue => ({
    dateLabel,
    userLabel,
    text,
  });

  return questions.map((question, index) => ({
    // TODO: use question id from BE
    id: index + 1,
    // https://github.com/orgs/waglabs/teams/webteam/discussions/13
    answer: isEmpty(question.answer) || isNil(question.answer) ? null : (
      veterinaryAdviceToAdviceAnswer(question.answer)
    ),
    header: veterinaryAdviceToAdviceHeader(question),
    question: veterinaryAdviceToAdviceQuestion(question),
    symptoms: mapSymptomList(question.symptoms),
    responses: question.comments.map(veterinaryAdviceToAdviceResponse),
    structuredData: question.structuredData,
  }));
};

const mapHealthExperienceList = (
  healthExpriences: VeterinaryAdviceHealthExperience[],
): HealthArticleSectionHealthExperienceListValue => {
  const veterinaryAdviceToAdviceHeader = ({
    age: ageLabel,
    breed,
    petName: name,
    numHelpful,
    upvoteApiUrl,
    downvoteApiUrl,
  }: VeterinaryAdviceQuestion): ArticleAdviceCardHeaderValue => ({
    ageLabel,
    breed,
    name,
    upvoteApiUrl,
    downvoteApiUrl,
    numberOfVotes: numHelpful,
  });
  const veterinaryAdviceToAdviceQuestion = ({
    text,
    // Omitting displaying of user and date label
  }: VeterinaryAdviceQuestion): Omit<ArticleAdviceQuestionValue, 'userLabel' | 'dateLabel'> => ({
    text,
  });
  return healthExpriences.map((healthExprience) => ({
    header: veterinaryAdviceToAdviceHeader(healthExprience),
    question: veterinaryAdviceToAdviceQuestion(healthExprience),
    symptoms: mapSymptomList(healthExprience.symptoms),
  }));
};

export const getArticleVetAdviceListTransformer = ({
  next_page: nextPage,
  prev_page: prevPage,
  health_experiences: healthExperiences,
  questions,
}: Input): Output => {
  const veterinaryAdvice = mapToVeterinaryAdvice({
    health_experiences: healthExperiences,
    questions,
  });

  return ({
    nextPage,
    prevPage,
    adviceList: mapQuestionsToAdviceList(veterinaryAdvice),
    healthExperienceList: mapHealthExperienceList(veterinaryAdvice.healthExperiences),
  });
};
